import type { LibraryKey, Tag, TagGroup, TagGroupDef } from '../types'
import { getSupabase } from './supabaseClient'

/**
 * Editing the library's vocabulary
 * -------------------------------------------------------------
 * The writes behind the Tag Manager. They are kept out of AssetRepository on
 * purpose: that interface is the read path and has a mock implementation for
 * working offline, and there is nothing useful to mock here — an edit that
 * only pretends to happen is worse than one that refuses.
 *
 * Every function is guarded twice over. The interface is hidden from anyone
 * who is not an administrator, and the row level security policies in
 * docs/tag-admin.sql refuse the write itself for anyone whose signed token
 * does not carry an administrator's address. This file relies on the second:
 * it does not check the role, it just asks, and reports the refusal.
 */

function client() {
  const supabase = getSupabase()
  if (!supabase) {
    throw new Error('Tag editing needs the database. This build is running on local files.')
  }
  return supabase
}

/**
 * The stable handle for a new tag.
 *
 * Names get corrected — `Sala Dressing` became `Salad Dressing` — so the slug
 * is what code and imports match on, and it is never touched again once the
 * tag exists. A rename deliberately leaves it alone.
 */
export function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export async function renameTag(id: string, name: string): Promise<void> {
  const trimmed = name.trim()
  if (!trimmed) throw new Error('A tag needs a name.')
  const { error } = await client().from('tags').update({ name: trimmed }).eq('id', id)
  if (error) throw new Error(error.message)
}

export async function setTagGroup(id: string, group: TagGroup): Promise<void> {
  const { error } = await client().from('tags').update({ group }).eq('id', id)
  if (error) throw new Error(error.message)
}

export async function createTag(
  name: string,
  group: TagGroup,
  /** Which library it belongs to. Its category must be one of that library's. */
  library: LibraryKey = 'audit',
): Promise<Tag> {
  const trimmed = name.trim()
  if (!trimmed) throw new Error('A tag needs a name.')
  const slug = slugify(trimmed)
  if (!slug) throw new Error('That name has no letters or numbers in it.')

  const { data, error } = await client()
    .from('tags')
    .insert({ slug, name: trimmed, group, library })
    .select('id, slug, name, group:"group"')
    .single()

  if (error) {
    // 23505 is the unique violation on slug. The name may well look different
    // from the one already there — `Squeeze Bottle` and `squeeze bottle` make
    // the same slug — so say which collision happened.
    if (error.code === '23505') {
      throw new Error(`A tag with the handle "${slug}" already exists in this library.`)
    }
    throw new Error(error.message)
  }

  const row = data as unknown as { id: string; slug: string; name: string; group: TagGroup }
  return { id: row.id, slug: row.slug, name: row.name, group: row.group, assetCount: 0 }
}

/**
 * Retire a tag.
 *
 * The interface only offers this on a tag nothing carries, and the count is
 * checked again here against the database rather than against whatever the
 * page last loaded — an import running in the Miro panel could have filed
 * something under it in the meantime, and the delete would cascade those rows
 * away without a word.
 */
export async function deleteTag(id: string): Promise<void> {
  const supabase = client()
  const { count, error: countError } = await supabase
    .from('asset_tags')
    .select('tag_id', { count: 'exact', head: true })
    .eq('tag_id', id)
  if (countError) throw new Error(countError.message)
  if ((count ?? 0) > 0) {
    throw new Error(
      `${count} image${count === 1 ? '' : 's'} now carry this tag. Merge it into another one instead.`,
    )
  }

  const { error } = await supabase.from('tags').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/**
 * Move every picture from one tag to another and retire the first.
 *
 * One database call, because the halfway point — both tags alive, the pictures
 * on both — is not a state the library should ever be left in. See
 * public.merge_tags in docs/tag-admin.sql.
 *
 * @returns how many pictures moved
 */
export async function mergeTags(sourceId: string, targetId: string): Promise<number> {
  const { data, error } = await client().rpc('merge_tags', {
    p_source: sourceId,
    p_target: targetId,
  })
  if (error) throw new Error(error.message)
  return typeof data === 'number' ? data : 0
}

/* -------------------------------------------------------------------------
   Putting a tag on pictures, and taking it off
   ------------------------------------------------------------------------- */

/** Postgres has a limit on how many rows one statement can carry comfortably. */
const CHUNK = 500

function chunked<T>(items: T[]): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += CHUNK) out.push(items.slice(i, i + CHUNK))
  return out
}

/**
 * Add a tag to every picture given.
 *
 * Pictures that already carry it are left alone rather than reported as an
 * error: selecting forty images and tagging them is a normal thing to do twice,
 * and the second time should be quiet.
 */
export async function addTagToAssets(tagId: string, assetIds: string[]): Promise<number> {
  if (assetIds.length === 0) return 0
  const supabase = client()
  for (const batch of chunked(assetIds)) {
    const { error } = await supabase
      .from('asset_tags')
      .upsert(
        batch.map((assetId) => ({ asset_id: assetId, tag_id: tagId })),
        { onConflict: 'asset_id,tag_id', ignoreDuplicates: true },
      )
    if (error) throw new Error(error.message)
  }
  return assetIds.length
}

export async function removeTagFromAssets(tagId: string, assetIds: string[]): Promise<number> {
  if (assetIds.length === 0) return 0
  const supabase = client()
  for (const batch of chunked(assetIds)) {
    const { error } = await supabase
      .from('asset_tags')
      .delete()
      .eq('tag_id', tagId)
      .in('asset_id', batch)
    if (error) throw new Error(error.message)
  }
  return assetIds.length
}

/* -------------------------------------------------------------------------
   The categories themselves
   -------------------------------------------------------------------------
   Adding a filter category used to mean editing a union type, two constants
   and a CHECK constraint, in three files that could not see each other. It is
   a row now. See docs/tag-groups.sql.
   ------------------------------------------------------------------------- */

/** Sparse positions, so a category can later be slotted between two others. */
const POSITION_STEP = 10

export async function createTagGroup(
  label: string,
  after: TagGroupDef[],
  library: LibraryKey = 'audit',
  /**
   * In front of the slug outside Audit (`dw-`): category slugs are unique
   * across every library, and Design Works has a Product of its own.
   */
  prefix = '',
): Promise<TagGroupDef> {
  const trimmed = label.trim()
  if (!trimmed) throw new Error('A category needs a name.')
  const bare = slugify(trimmed)
  if (!bare) throw new Error('That name has no letters or numbers in it.')
  const slug = `${prefix}${bare}`

  // Last in the filter, above the hidden ones. Somewhere is better than
  // nowhere, and moving it up is two clicks.
  const visible = after.filter((group) => !group.hidden)
  const position =
    visible.length > 0 ? Math.max(...visible.map((g) => g.position)) + POSITION_STEP : POSITION_STEP

  const { data, error } = await client()
    .from('tag_groups')
    .insert({ slug, label: trimmed, position, hidden: false, library })
    .select('slug, label, position, hidden')
    .single()

  if (error) {
    if (error.code === '23505') throw new Error(`A category with the handle "${slug}" already exists.`)
    throw new Error(error.message)
  }
  return data as unknown as TagGroupDef
}

export async function renameTagGroup(slug: string, label: string): Promise<void> {
  const trimmed = label.trim()
  if (!trimmed) throw new Error('A category needs a name.')
  const { error } = await client().from('tag_groups').update({ label: trimmed }).eq('slug', slug)
  if (error) throw new Error(error.message)
}

/**
 * Swap a category with its neighbour.
 *
 * Two writes rather than renumbering the whole list: only the pair being
 * exchanged changes, so two people reordering different parts of the filter at
 * the same time do not overwrite each other's work.
 */
export async function swapTagGroupPositions(a: TagGroupDef, b: TagGroupDef): Promise<void> {
  const supabase = client()
  const first = await supabase.from('tag_groups').update({ position: b.position }).eq('slug', a.slug)
  if (first.error) throw new Error(first.error.message)
  const second = await supabase.from('tag_groups').update({ position: a.position }).eq('slug', b.slug)
  if (second.error) throw new Error(second.error.message)
}

/**
 * Retire a category.
 *
 * No count is checked here on purpose. `tags.group` is a foreign key, so a
 * category with tags still in it is refused by the database itself — which is
 * the only check that cannot be raced by an import running in another tab.
 */
export async function deleteTagGroup(slug: string): Promise<void> {
  const { error } = await client().from('tag_groups').delete().eq('slug', slug)
  if (error) {
    // 23503: something still points at this row.
    if (error.code === '23503') {
      throw new Error('Tags are still filed under this category. Move them somewhere else first.')
    }
    throw new Error(error.message)
  }
}
